/**
 * The Change card review step's words (KPE-028, KPR-709). Every sentence the
 * review, its confirmation and the move's result say is made here, from the
 * dry run's and the move's counts (keepr-api SCHEMA.md § Change card), so the
 * copy Zach reviewed lives in one place and is tested without a DOM.
 *
 * `ctx` is `{ source, target, labels, targetLabels }`: the two cards' names
 * and the person's word for each element, by name (`labels` the source
 * card's, `targetLabels` the new card's). An element is named by its label,
 * never by its stored name.
 */

const num = value => (Number(value) || 0).toLocaleString('en-US');

/** "1 item", "3 items", "30,000 items". */
export function itemsText(count, singular = 'item', plural = `${singular}s`) {
	const n = Number(count) || 0;
	return `${num(n)} ${n === 1 ? singular : plural}`;
}

const isOne = count => Number(count) === 1;

/** "a", "a and b", "a, b, and c". */
export function andList(words = []) {
	const list = (words || []).filter(Boolean);
	if (list.length <= 1) return list[0] || '';
	if (list.length === 2) return `${list[0]} and ${list[1]}`;
	return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
}

const labelOf = (ctx, name) => (ctx?.labels && ctx.labels[name]) || name;
const namesOf = (ctx, names = []) => andList((names || []).map(name => labelOf(ctx, name)));
const sourceName = ctx => ctx?.source || 'the card they are on';
const targetName = ctx => ctx?.target || 'the new card';

const sumOf = (counts = {}) => Object.values(counts || {}).reduce((sum, n) => sum + (Number(n) || 0), 0);

// ── The page ────────────────────────────────────────────────────────────

export const REVIEW_LABEL = 'Review';
export const REVIEW_HEADING = 'Review the change';
export const BACK_TO_MAPPING = 'Back to the mapping';
export const CHANGE_CARD = 'Change card';
export const REVIEW_NEEDS_TARGET = 'Choose the card to move to first.';
export const REVIEW_NEEDS_FIXES = 'Fix the elements marked as problems first.';

// ── 1. What will move ──────────────────────────────────────────────────

/** "All 12 items will move to Log.", "9 of 12 items will move to Log." */
export function movingLine(items, ctx) {
	const evaluated = Number(items?.evaluated) || 0;
	const selected = Number(items?.selected) || evaluated;
	const moving = Number(items?.moving) || 0;
	if (!moving) return 'No item can move with this mapping. Go back to the mapping to change it.';
	if (moving === selected) {
		return isOne(moving) ? `The item will move to ${targetName(ctx)}.` : `All ${itemsText(moving)} will move to ${targetName(ctx)}.`;
	}
	return `${num(moving)} of ${itemsText(selected)} will move to ${targetName(ctx)}.`;
}

export function failingLead(failing) {
	return `${num(failing)} will not move. Change the mapping, or the values, to move ${isOne(failing) ? 'it' : 'them'}:`;
}

export function skippedLead(skipped, ctx) {
	return `${num(skipped)} ${isOne(skipped) ? 'is' : 'are'} skipped and stay${isOne(skipped) ? 's' : ''} on ${sourceName(ctx)}:`;
}

/** Why an item is skipped, said to the person reviewing. */
export function skipReasonWords(reason, ctx) {
	switch (reason) {
		case 'notFound': return 'Deleted, or no longer there';
		case 'otherCard': return 'On another card';
		case 'outsideFamily': return `In a collection ${targetName(ctx)} is not used in`;
		case 'pending': return 'Waiting for review';
		case 'cannotEdit': return 'You cannot change it';
		case 'locked': return 'Locked';
		case 'hiddenValues': return 'Holds a value you cannot see';
		case 'restrictedValues': return 'Holds a value only some people may change';
		case 'changedSince': return 'Changed since you reviewed';
		case 'itemTag': return 'Used as a tag';
		default: return 'Skipped';
	}
}

/** The skips that happened, `[{ reason, count, words }]`, largest first. */
export function skippedRows(skipped = {}, ctx) {
	return Object.entries(skipped || {})
		.filter(([, n]) => Number(n) > 0)
		.sort((a, b) => b[1] - a[1])
		.map(([reason, count]) => ({ reason, count, words: skipReasonWords(reason, ctx) }));
}

export const skippedTotal = sumOf;

export const BACKGROUND_LINE = 'This change runs in the background. You can leave the page while it runs, and follow it in Settings → Imports.';

export function moreLine(count) {
	return `and ${num(count)} more`;
}

// ── 2. Where each element goes ─────────────────────────────────────────

export function sourcesTitle(ctx) {
	return `From ${sourceName(ctx)} to ${targetName(ctx)}`;
}

/** One source element: "Hours → Count · on 12 items · 1 will not convert". */
export function sourceRowParts(row, ctx) {
	const holding = Number(row?.holding) || 0;
	const goesTo = (row?.goesTo || []).map(name => (ctx?.targetLabels && ctx.targetLabels[name]) || labelOf(ctx, name));
	const label = row?.label || row?.element;
	if (!holding) return { label, goesTo, meta: ['no item has a value'] };
	const meta = [`on ${itemsText(holding)}`];
	if (!goesTo.length) meta.push('left behind');
	if (Number(row?.notConverted) > 0) meta.push(`${num(row.notConverted)} will not convert`);
	return { label, goesTo, meta };
}

// ── 3. Left behind ─────────────────────────────────────────────────────

export function leftBehindIntro(ctx) {
	return `Nothing on ${targetName(ctx)} takes these elements. Choose what happens to their values.`;
}

/** "4 items have a value in Mood that is not moved." */
export function leftBehindSentence(holding, label) {
	return `${itemsText(holding)} ${isOne(holding) ? 'has' : 'have'} a value in ${label} that is not moved.`;
}

export const ADD_TO_COMMENT = 'Add to a comment';
export const LOSE_LABEL = 'Lose';

export function commentHelp(label, ctx) {
	return `Written into one comment on each item, as “Moved from ${sourceName(ctx)}: ${label}: …”.`;
}

export const LOSE_HELP = 'Taken off the item and kept in History only.';
export const FILE_HELP = 'Its files stay on the item as attachments.';

export function restrictedHelp(label) {
	return `Only some people can see ${label}, and everyone who reads an item reads its comments. Its value is taken off the item and kept in History only.`;
}

// ── 4. Warnings ────────────────────────────────────────────────────────

const REVERT = 'You can revert the change to get them back.';

/** The codes whose values leave the item, kept in History only. */
export const LOSS_CODES = ['comments_off', 'lose', 'comment_restricted', 'removed_values'];

/**
 * One dry run warning as the review says it: `{ code, text, link? }`. A
 * code this list does not know shows the API's own sentence.
 */
export function warningText(warning, ctx) {
	const items = Number(warning?.items) || 0;
	const elements = warning?.elements || [];
	switch (warning?.code) {
		case 'comments_off': {
			const what = elements.length ? `the values of ${namesOf(ctx, elements)}` : 'the values left behind';
			const text = `Comments are off on ${targetName(ctx)}, so ${what} on ${itemsText(items)} will not go into a comment. They are taken off the items and kept in History only. ${REVERT}`;
			const to = warning.canEnableComments ? (warning.cardAddress || null) : null;
			return { code: warning.code, text, link: warning.canEnableComments ? { to, label: `Turn on comments for ${targetName(ctx)}` } : null };
		}
		case 'lose':
			return {
				code: warning.code,
				text: `You chose to lose the values of ${namesOf(ctx, elements)} on ${itemsText(items)}. They are taken off the items and kept in History only. ${REVERT}`,
			};
		case 'comment_restricted':
			return {
				code: warning.code,
				text: `On ${itemsText(items)}, only some people can see a value that was to go into a comment, and everyone who reads the item reads its comments. It is taken off the item and kept in History only. You can revert the change to get it back.`,
			};
		case 'removed_values':
			return {
				code: warning.code,
				text: `${itemsText(items)} ${isOne(items) ? 'holds' : 'hold'} values of elements ${sourceName(ctx)} no longer has. They do not move to ${targetName(ctx)}. They are kept in History only, and you can revert the change to get them back.`,
			};
		case 'restricted_values':
			return {
				code: warning.code,
				text: `${itemsText(items)} ${isOne(items) ? 'holds' : 'hold'} a value only some people may change, so ${isOne(items) ? 'it is' : 'they are'} not moved. Someone the element's rule lets change it can move ${isOne(items) ? 'it' : 'them'}, or the rule can be changed.`,
			};
		case 'signature':
			return {
				code: warning.code,
				text: `${targetName(ctx)} asks for a signature on every change. You sign once, when you change the card, and it covers every item.`,
			};
		default:
			return { code: warning?.code || 'warning', text: warning?.message || '' };
	}
}

/** Whether any of the warnings means a value leaves the item. */
export function hasLoss(warnings = []) {
	return (warnings || []).some(w => LOSS_CODES.includes(w?.code) && Number(w?.items) > 0);
}

/** The short loss sentences of the confirmation, one per kind of loss. */
export function lossShortLines(warnings = [], ctx) {
	return (warnings || []).filter(w => LOSS_CODES.includes(w?.code) && Number(w?.items) > 0).map(w => {
		const on = itemsText(w.items);
		switch (w.code) {
			case 'lose':
			case 'comments_off':
				return w.elements?.length
					? `The values of ${namesOf(ctx, w.elements)} on ${on} are kept in History only.`
					: `Values on ${on} are kept in History only.`;
			case 'comment_restricted':
				return `A value on ${on} is kept in History only.`;
			default:
				return `Values of removed elements on ${on} are kept in History only.`;
		}
	});
}

// ── 5. Give a value ────────────────────────────────────────────────────

export const GIVE_VALUE_TITLE = 'Give a value';

export function giveValueIntro(ctx) {
	return `${targetName(ctx)} needs a value in these elements, and nothing you mapped fills them. The value you give goes on every item that moves.`;
}

export function giveValueFirst(labels = []) {
	return `Give a value for ${andList(labels)} first.`;
}

// ── 6. Blocked by lookups ──────────────────────────────────────────────

export const BLOCKED_TITLE = 'These items cannot move yet';

export function blockedBody(ctx) {
	return `Items elsewhere point at them through a lookup that will not accept them on ${targetName(ctx)}. Change these lookups first, then check again.`;
}

/** "Note · 3 items", "Steps › Ref · 3 items", "… from the About set". */
export function lookupLine(lookup) {
	const where = lookup?.column ? `${lookup.element} › ${lookup.column}` : lookup?.element;
	const set = lookup?.set?.name ? ` from the ${lookup.set.name} set` : '';
	return `${where}${set} · ${itemsText(lookup?.items)}`;
}

export function hiddenLookupLine(count) {
	return `and ${itemsText(count, 'more item', 'more items')} through a lookup you cannot see.`;
}

export const CHECK_AGAIN = 'Check again';
export const BLOCKED_FIRST = 'Change the lookups first.';

// ── 7. What stops applying ─────────────────────────────────────────────

export const DEPENDENTS_TITLE = 'What stops applying';

export function dependentsIntro(ctx) {
	return `These name ${sourceName(ctx)} or its elements. Nothing here is changed: each stops applying to the items that move.`;
}

export const DEPENDENT_KINDS = [
	['savedFilters', 'Saved filters'],
	['views', 'Layouts'],
	['rules', 'Rules'],
	['charts', 'Charts'],
	['dashboards', 'Dashboards'],
	['quickAdds', 'Quick adds'],
	['multiAddForms', 'Multi add forms'],
	['forms', 'Public forms'],
	['notifications', 'Notifications'],
	['access', 'Access (grants, element rules, links, groups)'],
];

const SHOWN_NAMES = 3;

/** One kind's line: `{ kind, label, count, names, hidden }`, or null at zero. */
export function dependentRow(kind, label, entry) {
	const count = Number(entry?.count) || 0;
	if (!count) return null;
	const hidden = Number(entry?.hidden) || 0;
	const named = (entry?.named || []).map(row => row?.name).filter(Boolean);
	const shown = named.slice(0, SHOWN_NAMES);
	const more = Math.max(0, count - hidden - shown.length);
	const names = shown.length ? (more ? `${shown.join(', ')} and ${num(more)} more` : andList(shown)) : null;
	return { kind, label, count, names, hidden: hidden ? `${num(hidden)} you cannot see` : null };
}

export function dependentRows(dependents = {}) {
	return DEPENDENT_KINDS.map(([kind, label]) => dependentRow(kind, label, dependents?.[kind])).filter(Boolean);
}

export function noDependents(ctx) {
	return `Nothing else names ${sourceName(ctx)}.`;
}

// ── 8. Rules ───────────────────────────────────────────────────────────

export function rulesTitle(ctx) {
	return `Rules on ${targetName(ctx)}`;
}

export function rulesIntro(ctx) {
	return `These rules run when an item is added to ${targetName(ctx)}. Choose which run on the items you move. They run as you, after the move. Rules that only change data are ticked; rules that notify, email or touch other records are not.`;
}

export const RULE_NOT_MANAGED = 'A rule you do not manage';
export const ALWAYS_RUNS = 'Always runs on a card change';
export const ALWAYS_RUNS_WHY = 'A manager set this rule to always run, because it keeps other records right.';
export const NOT_CHOOSABLE_WHY = 'Only a manager of its collection chooses whether it runs.';
export const MATCH_UNKNOWN = 'keepr cannot count which items match until they move.';

export function matchedLine(matched) {
	if (matched === null || matched === undefined) return MATCH_UNKNOWN;
	return `${num(matched)} of the moving items match`;
}

export function noRules(ctx) {
	return `No rule on ${targetName(ctx)} runs when an item is added.`;
}

/** "keepr also works these out on every item: Number is numbered, Total is recalculated." */
export function keeprWritesLine(writes = []) {
	const parts = (writes || []).map(w => `${w.label || w.element} is ${w.at === 'numbered' ? 'numbered' : 'recalculated'}`);
	return parts.length ? `keepr also works these out on every item: ${parts.join(', ')}.` : null;
}

// ── 9. Preview ─────────────────────────────────────────────────────────

export const PREVIEW_TITLE = 'Preview';
export const PREVIEW_INTRO = 'A few of the items, as they are and as they will be.';

export function previewNow(ctx) {
	return `Now, on ${sourceName(ctx)}`;
}

export function previewAfter(ctx) {
	return `After, on ${targetName(ctx)}`;
}

export const LEFT_BEHIND_PREFIX = 'Left behind:';
export const WILL_NOT_MOVE_PREFIX = 'Will not move:';

// ── 10. The confirmation ───────────────────────────────────────────────

/**
 * The one sentence: "Move 9 items from Note to Log. 3 will not move and 2
 * are skipped; they stay on Note." with the loss sentences after it.
 */
export function confirmSentence(items, warnings, ctx) {
	const moving = Number(items?.moving) || 0;
	const failing = Number(items?.failing) || 0;
	const skipped = sumOf(items?.skipped);
	const parts = [`Move ${itemsText(moving)} from ${sourceName(ctx)} to ${targetName(ctx)}.`];
	const rest = [];
	if (failing) rest.push(`${num(failing)} will not move`);
	if (skipped) rest.push(`${num(skipped)} ${isOne(skipped) ? 'is' : 'are'} skipped`);
	if (rest.length) {
		const stay = failing + skipped;
		parts.push(`${rest.join(' and ')}; ${isOne(stay) ? 'it stays' : 'they stay'} on ${sourceName(ctx)}.`);
	}
	return [...parts, ...lossShortLines(warnings, ctx)].join(' ');
}

export const LOSE_WORD = 'lose';
export const LOSE_CONFIRM_TITLE = 'Change card and lose values?';

// ── 11. After the change ───────────────────────────────────────────────

export function movedLine(moved, ctx) {
	return `Moved ${itemsText(moved)} to ${targetName(ctx)}.`;
}

export function couldNotMoveLead(failed) {
	return `${num(failed)} could not move:`;
}

export function wereSkippedLead(skipped) {
	return `${num(skipped)} ${isOne(skipped) ? 'was' : 'were'} skipped:`;
}

/** Where the moved items' data left behind went (`leftBehind` of the move). */
export function leftBehindResultLines(leftBehind = {}) {
	const lines = [];
	const comments = Number(leftBehind?.comments?.items) || 0;
	const history = Number(leftBehind?.history?.items) || 0;
	const attachments = Number(leftBehind?.attachments?.items) || 0;
	if (comments) lines.push(`What was left behind is in a comment on ${itemsText(comments)}.`);
	if (history) lines.push(`Kept in History only on ${itemsText(history)}.`);
	if (attachments) lines.push(`Files stayed on ${itemsText(attachments)} as attachments.`);
	return lines;
}

/** One rule of the move's report: "Count the logs ran on 9 items." */
export function ruleResultLine(rule) {
	const name = rule?.name || RULE_NOT_MANAGED;
	if (Number(rule?.ran) > 0) return `${name} ran on ${itemsText(rule.ran)}.`;
	if (rule?.reason === 'unticked' || rule?.ticked === false) return `${name} did not run: you did not tick it.`;
	if (rule?.reason === 'changed') return `${name} did not run: it was changed during the move.`;
	if (rule?.reason === 'gone') return `${name} did not run: it was paused or deleted since.`;
	if (rule?.reason === 'stopped') return `${name} did not run on every item: the rules ran out of time. What it owes is finished later.`;
	return `${name} did not run on any item.`;
}

export const GO_TO_ITEMS = 'Go to the items';
export const UNDO = 'Undo';
export const UNDO_CARD_CHANGE = 'Undo card change';
export const UNDO_CONFIRM_TITLE = 'Undo this card change?';

export function undoConfirmBody(moved, ctx) {
	return `The ${itemsText(moved)} go back to ${sourceName(ctx)}, each with every value it had there. Values entered on ${targetName(ctx)} since are kept in History. An item whose card changed again since is left as it is.`;
}

export function undoneLine(reverted, failed, ctx) {
	const back = `${itemsText(reverted)} went back to ${sourceName(ctx)}.`;
	return failed ? `${back} ${num(failed)} could not:` : back;
}

export function backgroundMovingLine(total, ctx) {
	return `Moving ${itemsText(total)} to ${targetName(ctx)} in the background.`;
}

export function backgroundProgressLine(progress = {}) {
	return `${num(progress.done)} of ${itemsText(progress.total)} worked through. You can leave this page; it carries on.`;
}

export const SEE_IN_IMPORTS = 'See it in Imports';

// ── Refusals ───────────────────────────────────────────────────────────

const NOTHING_CHANGED = 'Nothing was changed.';

/**
 * A refused move, in words: `{ text, review }` — `review` true when the
 * answer is to review the change again (the page runs the dry run again).
 */
export function refusalText(code, message, ctx) {
	switch (code) {
		case 'change_card_rules_changed':
			return { text: `The rules on ${targetName(ctx)} changed since you reviewed. Review them again.`, review: true };
		case 'change_card_cards_changed':
			return { text: `${sourceName(ctx)} or ${targetName(ctx)} changed since you reviewed. Review the change again.`, review: true };
		case 'change_card_token_expired':
			return { text: 'This review is more than 30 minutes old. Review the change again.', review: true };
		case 'change_card_token_used':
		case 'change_card_token_invalid':
		case 'change_card_token_mismatch':
			return { text: 'This review is no longer current. Review the change again.', review: true };
		case 'change_card_task_running':
			return { text: 'Another card change is running in this collection. Try again when it has finished.', review: false };
		default:
			return { text: message ? `${message} ${NOTHING_CHANGED}` : `The card could not be changed. ${NOTHING_CHANGED}`, review: false };
	}
}
